<script setup lang="ts">
import { Document } from 'flexsearch';
import {
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxRoot,
  ComboboxViewport,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogOverlay,
  DialogPortal,
  DialogRoot,
  DialogTitle,
} from 'reka-ui';
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useRouter } from 'vue-router';

import { KaButton } from '@/kasten-components';
import { useMainStore } from '@/stores/main';
import { useProfileStore } from '@/stores/profiles/profiles';
import { routeToResolvedGeneric } from '@/utils/cluster-utils';
import SearchIcon from '~icons/lucide/search';
import XIcon from '~icons/lucide/x';

const props = defineProps<{
  fullWidth?: boolean;
}>();

interface SearchItem {
  id: string;
  title: string;
  cr: string;
  path: string;
  [key: string]: string;
}

const router = useRouter();
const locationProfileStore = useProfileStore();
const mainStore = useMainStore();
const searchTerm = ref('');
const recentSearches = ref<SearchItem[]>([]);
const isModalOpen = ref(false);
const selectedValue = ref<SearchItem | undefined>();

const namespaceItems = computed<SearchItem[]>(() => {
  return (
    mainStore.namespaces?.map((ns) => ({
      id: ns?.name || '',
      title: ns?.name || '',
      cr: 'application',
      path: routeToResolvedGeneric({
        name: 'Apps',
        query: {
          applicationName: ns?.name,
        },
      }).fullPath,
    })) || []
  );
});

const policyItems = computed<SearchItem[]>(() => {
  return (
    mainStore.policies?.map((policy) => ({
      id: policy?.metadata?.name || '',
      title: policy?.metadata?.name || '',
      cr: 'policy',
      path: routeToResolvedGeneric({
        name: 'PolicyView',
        params: {
          namespace: policy?.metadata?.namespace || '',
          name: policy?.metadata?.name || '',
        },
      }).fullPath,
    })) || []
  );
});

const locationProfileItems = computed<SearchItem[]>(() => {
  return (
    locationProfileStore.profilesLocation?.map((profile) => ({
      id: profile?.metadata?.name || '',
      title: profile?.metadata?.name || '',
      cr: 'location',
      path: routeToResolvedGeneric({
        name: 'LocationProfileView',
        params: {
          namespace: profile?.metadata?.namespace || '',
          name: profile?.metadata?.name || '',
        },
      }).fullPath,
    })) || []
  );
});

const infraProfileItems = computed<SearchItem[]>(() => {
  return (
    locationProfileStore.profilesInfra?.map((profile) => ({
      id: profile?.metadata?.name || '',
      title: profile?.metadata?.name || '',
      cr: 'infra',
      path: routeToResolvedGeneric({
        name: 'InfraProfiles',
      }).fullPath,
    })) || []
  );
});

const allItems = computed<SearchItem[]>(() => {
  return [
    ...namespaceItems.value,
    ...policyItems.value,
    ...locationProfileItems.value,
    ...infraProfileItems.value,
  ].filter((item) => item.id && item.title);
});

const searchIndex = computed(() => {
  const index = new Document<SearchItem>({
    document: { id: 'id', index: ['title'] },
    tokenize: 'full',
    resolution: 9,
    fastupdate: false,
  });

  if (allItems.value.length > 0) {
    allItems.value.forEach((doc) => {
      if (doc.id) index.add(doc);
    });
  }

  return index;
});

// Filtered items based on query
const filteredItems = computed(() => {
  if (!searchTerm.value.trim()) {
    return recentSearches.value.length > 0 ? recentSearches.value : allItems.value;
  }

  const searchResults = searchIndex.value.search(searchTerm.value);
  const resultIds = new Set(searchResults.flatMap((result) => result.result));

  return allItems.value.filter((item) => resultIds.has(item.id));
});

const handleItemSelection = (item: SearchItem) => {
  if (!item?.id) return;

  // Add to recent searches
  const filteredSearches = recentSearches.value.filter((search) => search.id !== item.id);
  recentSearches.value = [item, ...filteredSearches.slice(0, 4)];
  localStorage.setItem('recentSearches', JSON.stringify(recentSearches.value));

  // Navigate
  if (item.path) {
    router.push(item.path);
  }

  closeModal();
};

const closeModal = () => {
  isModalOpen.value = false;
  searchTerm.value = '';
  selectedValue.value = undefined;
};

const handleOpenChange = (open: boolean) => {
  isModalOpen.value = open;
  if (!open) {
    searchTerm.value = '';
    selectedValue.value = undefined;
  }
};

const handleKeyboardShortcut = (event: KeyboardEvent) => {
  // Check for cmd+K or ctrl+K
  if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
    event.preventDefault();
    isModalOpen.value = true;
  }
};

// Load recent searches from localStorage
onMounted(() => {
  const storedSearches = localStorage.getItem('recentSearches');
  if (storedSearches) {
    try {
      recentSearches.value = JSON.parse(storedSearches);
    } catch (e) {
      localStorage.removeItem('recentSearches');
    }
  }

  document.addEventListener('keydown', handleKeyboardShortcut);
});

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeyboardShortcut);
});
</script>

<template>
  <KaButton
    :class="{ 'w-full': props.fullWidth }"
    data-testid="search-modal-button"
    @click="isModalOpen = true"
  >
    <slot name="icon"> <SearchIcon class="size-3.5" /></slot>
    <div
      :class="[
        'flex font-normal',
        props.fullWidth ? 'w-full justify-between' : 'min-w-28 justify-between',
      ]"
    >
      <span>Search</span>
      <kbd
        class="inline-flex items-center bg-transparent px-1.5 py-0.5 font-sans text-gray-700 dark:text-gray-300"
      >
        ⌘ K
      </kbd>
    </div>
  </KaButton>

  <DialogRoot :open="isModalOpen" @update:open="handleOpenChange">
    <DialogPortal>
      <Transition
        enter-active-class="transition duration-300 ease-out"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition duration-200 ease-in"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <DialogOverlay
          v-if="isModalOpen"
          class="fixed inset-0 z-50 bg-black/30 backdrop-blur-sm dark:bg-black/50"
        />
      </Transition>
      <Transition
        enter-active-class="transition duration-300 ease-out"
        enter-from-class="opacity-0 scale-95"
        enter-to-class="opacity-100 scale-100"
        leave-active-class="transition duration-200 ease-in"
        leave-from-class="opacity-100 scale-100"
        leave-to-class="opacity-0 scale-95"
      >
        <DialogContent
          v-if="isModalOpen"
          class="fixed left-[50%] top-[20%] z-50 w-[90vw] max-w-3xl -translate-x-1/2 overflow-hidden rounded-lg border border-solid border-navy-800/10 bg-white p-0 shadow-[0_1px_4px_rgba(37,39,59,0.06)] dark:border-navy-700/50 dark:bg-navy-900 dark:shadow-[0_1px_4px_rgba(0,0,0,0.2)] sm:w-3/5"
        >
          <DialogTitle class="sr-only">Search</DialogTitle>
          <DialogDescription class="sr-only">
            Search Kasten applications, policies, and profiles.
          </DialogDescription>
          <ComboboxRoot
            v-model="selectedValue"
            :open="true"
            @update:model-value="handleItemSelection"
          >
            <ComboboxInput
              v-model="searchTerm"
              data-testid="search-combobox-input"
              placeholder="Search Applications, Policies, and Profiles"
              class="w-full border-none py-4 pl-6 pr-14 text-base shadow-none outline-none ring-0 focus:shadow-none focus:ring-0 dark:bg-navy-900 sm:text-sm"
              auto-focus
              @keydown.esc="closeModal"
            />

            <DialogClose
              class="absolute right-6 top-3 rounded p-1 text-gray-400 hover:text-gray-500 dark:text-gray-500 dark:hover:text-gray-400"
              aria-label="Close"
            >
              <XIcon class="h-5 w-5" />
            </DialogClose>

            <hr class="m-0 border-gray-200 p-0 dark:border-navy-700" />

            <div
              v-if="searchTerm.trim() === '' && recentSearches.length > 0"
              class="-mb-2 mt-1 px-6 py-2 text-xs font-medium text-gray-500 dark:text-gray-400"
            >
              Recent Searches
            </div>

            <ComboboxContent position="inline" class="relative">
              <ComboboxViewport
                class="z-10 mb-0 max-h-80 w-full overflow-auto bg-white p-0 py-2 text-sm focus:outline-none dark:bg-navy-900"
              >
                <ComboboxItem
                  v-for="item in filteredItems"
                  :key="item.id"
                  :value="item"
                  :text-value="item.title"
                  class="group relative w-full cursor-pointer select-none px-6 py-2 text-left text-gray-900 data-[highlighted]:bg-blue-500/10 data-[highlighted]:text-gray-900 dark:text-gray-200 dark:data-[highlighted]:bg-blue-500/20 dark:data-[highlighted]:text-white"
                >
                  <span class="block truncate font-semibold">
                    {{ item.title }}
                  </span>

                  <span class="absolute inset-y-0 right-0 flex items-center pr-3">
                    <span
                      class="inline-flex items-center rounded bg-gray-100/20 px-2.5 py-0.5 text-xs font-medium text-gray-500 ring-1 ring-gray-200 group-data-[highlighted]:bg-blue-400 group-data-[highlighted]:text-white dark:bg-navy-800/70 dark:text-gray-500 dark:ring-navy-700 dark:group-data-[highlighted]:bg-blue-600"
                    >
                      {{ item.cr }}
                    </span>
                  </span>
                </ComboboxItem>

                <ComboboxEmpty
                  class="relative cursor-default select-none px-4 py-16 text-center text-gray-700 dark:text-gray-300"
                >
                  No results for <span class="font-semibold">{{ searchTerm }}</span>
                </ComboboxEmpty>
              </ComboboxViewport>
            </ComboboxContent>

            <div
              class="flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-solid border-gray-800/10 px-4 py-3 text-xs dark:border-navy-700 sm:gap-4 sm:px-6 sm:py-4 sm:text-sm"
            >
              <span
                class="inline-flex items-center gap-1 whitespace-nowrap leading-4 text-gray-700 dark:text-gray-300"
              >
                <kbd
                  class="inline-block rounded bg-gray-100 px-1 py-0.5 text-xs font-medium text-gray-700 shadow-sm ring-1 ring-gray-300/50 dark:bg-navy-700 dark:text-gray-300 dark:ring-navy-600/50 sm:rounded-md sm:px-1.5 sm:text-sm"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="1em"
                    height="1em"
                    viewBox="0 0 24 24"
                    class="h-2.5 w-2.5 sm:h-3.5 sm:w-3.5"
                    stroke="currentColor"
                    stroke-width="2"
                    fill="none"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <path d="m5 12l7-7l7 7m-7 7V5" />
                  </svg>
                </kbd>
                <kbd
                  class="inline-block rounded bg-gray-100 px-1 py-0.5 text-xs font-medium text-gray-700 shadow-sm ring-1 ring-gray-300/50 dark:bg-navy-700 dark:text-gray-300 dark:ring-navy-600/50 sm:rounded-md sm:px-1.5 sm:text-sm"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="1em"
                    height="1em"
                    viewBox="0 0 24 24"
                    class="h-2.5 w-2.5 sm:h-3.5 sm:w-3.5"
                    stroke="currentColor"
                    stroke-width="2"
                    fill="none"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <path d="M12 5v14m7-7l-7 7l-7-7" />
                  </svg>
                </kbd>
                to navigate
              </span>
              <span
                class="inline-flex items-center gap-1 whitespace-nowrap leading-4 text-gray-700 dark:text-gray-300"
              >
                <kbd
                  class="inline-block rounded bg-gray-100 px-1 py-0.5 text-xs font-medium text-gray-700 shadow-sm ring-1 ring-gray-300/50 dark:bg-navy-700 dark:text-gray-300 dark:ring-navy-600/50 sm:rounded-md sm:px-1.5 sm:text-sm"
                >
                  enter
                </kbd>
                to select
              </span>
              <span
                class="inline-flex items-center gap-1 whitespace-nowrap leading-4 text-gray-700 dark:text-gray-300"
              >
                <kbd
                  class="inline-block rounded bg-gray-100 px-1 py-0.5 text-xs font-medium text-gray-700 shadow-sm ring-1 ring-gray-300/50 dark:bg-navy-700 dark:text-gray-300 dark:ring-navy-600/50 sm:rounded-md sm:px-1.5 sm:text-sm"
                >
                  esc
                </kbd>
                to close
              </span>
            </div>
          </ComboboxRoot>
        </DialogContent>
      </Transition>
    </DialogPortal>
  </DialogRoot>
</template>
